import {useMutation, useQueryClient} from '@github-ui/react-query'
import {verifiedFetch} from '@github-ui/verified-fetch'
import type {ListsUpdateResponse, StarState} from '../types'
import {parseStarCount} from '../utils/parse-star-count'
import {userListsQueryKey} from './use-user-lists-query'

interface ListUpdateInput {
  listIds: number[]
}

interface ListUpdateContext {
  previousStarState: StarState
  didOptimisticStar: boolean
}

interface UseListUpdateMutationOptions {
  listsApiPath: string
  listsUpdatePath: string
  repoStarred: boolean
  starCount: number
  onStarStateChange: (next: StarState) => void
}

/**
 * Hook to update list membership for a repository.
 * Performs optimistic star when adding an unstarred repo to its first list.
 * Automatically invalidates the user-lists query on settlement.
 */
export function useListUpdateMutation({
  listsApiPath,
  listsUpdatePath,
  repoStarred,
  starCount,
  onStarStateChange,
}: UseListUpdateMutationOptions) {
  const queryClient = useQueryClient()

  return useMutation<ListsUpdateResponse | null, Error, ListUpdateInput, ListUpdateContext>({
    mutationFn: async ({listIds}) => {
      const body = new URLSearchParams()
      body.append('context', 'user_list_menu')
      body.append('list_ids[]', '')
      for (const id of listIds) {
        body.append('list_ids[]', String(id))
      }

      const response = await verifiedFetch(listsUpdatePath, {
        method: 'PUT',
        body,
        headers: {Accept: 'application/json'},
      })

      if (!response.ok) {
        throw new Error('Failed to update lists')
      }

      try {
        return (await response.json()) as ListsUpdateResponse
      } catch {
        return null
      }
    },

    onMutate: ({listIds}) => {
      const didOptimisticStar = !repoStarred && listIds.length > 0
      const previousStarState = {starred: repoStarred, starCount}

      if (didOptimisticStar) {
        onStarStateChange({starred: true, starCount: starCount + 1})
      }

      return {previousStarState, didOptimisticStar}
    },

    onSuccess: (payload, _variables, context) => {
      if (!context) return
      if (payload?.didStar) {
        onStarStateChange({
          starred: true,
          starCount: parseStarCount(payload.starCount) ?? starCount + 1,
        })
      }
    },

    onError: (_error, _variables, context) => {
      if (context?.didOptimisticStar) {
        onStarStateChange(context.previousStarState)
      }
    },

    onSettled: () => {
      void queryClient.invalidateQueries({queryKey: userListsQueryKey(listsApiPath)})
    },
  })
}
